// WHAT: Widget component for displaying Parqet portfolio summary
// WHY:  Provides dashboard overview of investment portfolio with total invested and position count
// DOMAIN: Core → Dashboard → Widgets → Investments
// DEPENDENCIES: usePortfolioPositions hook
// RISKS:
//   - NOTE: Total invested calculation may not account for all position types
//   - NOTE: Currency formatting is hardcoded to EUR - may not match user currency
//   - NOTE: Loading state may flash on mount
//   - NOTE: Error handling may not provide user-friendly feedback

import { TrendingUp } from "lucide-react";
import { type FC, useMemo } from "react";

import { Text, Heading, Card, Grid } from "@/components/ui";
import { usePortfolioPositions } from "@/domains/investments/parqet/hooks/usePortfolioPositions";

import { useWidgetConfig } from "../../../hooks/useWidgetConfig";
import { WIDGET_IDS } from "../../../store/widgetRegistry";
import { WidgetContainer } from "../base";

/**
 * WHAT:
 *   Widget component that displays Parqet portfolio summary including
 *   total invested amount and position count.
 *
 * WHY:
 *   Enables users to see their investment portfolio overview at a glance
 *   on the dashboard.
 *
 * BEHAVIOR:
 *   - Fetches portfolio positions
 *   - Calculates total invested amount
 *   - Displays position count
 *   - Formats currency in EUR
 *   - Handles loading and error states
 *
 * RISKS:
 *   - NOTE: Total invested calculation may not account for all position types
 *   - NOTE: Currency formatting is hardcoded to EUR - may not match user currency
 *   - NOTE: Loading state may flash on mount
 *   - NOTE: Error handling may not provide user-friendly feedback
 *
 * @returns {JSX.Element} Parqet summary widget component
 *
 */
export const ParqetSummaryWidget: FC = () => {
  const { config } = useWidgetConfig(WIDGET_IDS.PARQET_SUMMARY);
  const { error, loading, positions } = usePortfolioPositions();

  /**
   * WHAT:
   *   Calculates total invested amount from all positions.
   *
   * WHY:
   *   Provides aggregate investment value.
   */
  const totalInvested = useMemo(() => {
    return positions.reduce((sum, pos) => sum + (pos.invested_amount || 0), 0);
  }, [positions]);

  const totalPositions = positions.length;

  return (
    <WidgetContainer
      description="Portfolio summary"
      error={error}
      icon={<TrendingUp className="w-5 h-5" />}
      loading={loading}
      size={config?.size ?? "medium"}
      title="Parqet Portfolio"
      widgetId={WIDGET_IDS.PARQET_SUMMARY}
    >
      <Grid cols={{ sm: 2 }} gap={3}>
        <Card className="text-center" padding="sm" variant="default">
          <Text className="text-xs text-gray-400 mb-1">Total Invested</Text>
          <Heading className="text-xl font-bold text-white" level={4}>
            {totalInvested.toLocaleString("de-DE", {
              currency: "EUR",
              minimumFractionDigits: 0,
              style: "currency",
            })}
          </Heading>
        </Card>

        <Card className="text-center" padding="sm" variant="default">
          <Text className="text-xs text-gray-400 mb-1">Positions</Text>
          <Heading className="text-xl font-bold text-white" level={4}>
            {totalPositions}
          </Heading>
        </Card>
      </Grid>
    </WidgetContainer>
  );
};
